/*!
 * notation.js — Lightweight SVG notation renderer for Oscilla score fragments
 * Generates staff + noteheads + accidentals + ledger lines + microtone labels
 * Uses Bravura (SMuFL) font for proper music glyphs.
 * © 2026 Rob Canning — GPLv3
 */

const SVG_NS = "http://www.w3.org/2000/svg";

// ============================================================
// Constants
// ============================================================

const LETTER_INDEX = { C: 0, D: 1, E: 2, F: 3, G: 4, A: 5, B: 6 };
const BASE_SEMI    = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// Diatonic number of the bottom staff line for each clef
const CLEF_BOTTOM = {
  treble: 4 * 7 + 2,  // E4 = 30
  bass:   2 * 7 + 4,  // G2 = 18
};

// Preferred enharmonic spelling per pitch class (0-11)
const SHARP_SPELL = [
  { l: "C", a: "" },  { l: "C", a: "#" }, { l: "D", a: "" },  { l: "D", a: "#" },
  { l: "E", a: "" },  { l: "F", a: "" },  { l: "F", a: "#" }, { l: "G", a: "" },
  { l: "G", a: "#" }, { l: "A", a: "" },  { l: "A", a: "#" }, { l: "B", a: "" },
];
const FLAT_SPELL = [
  { l: "C", a: "" },  { l: "D", a: "b" }, { l: "D", a: "" },  { l: "E", a: "b" },
  { l: "E", a: "" },  { l: "F", a: "" },  { l: "G", a: "b" }, { l: "G", a: "" },
  { l: "A", a: "b" }, { l: "A", a: "" },  { l: "B", a: "b" }, { l: "B", a: "" },
];

// Transposition: writtenMIDI = soundingMIDI + offset
const TRANSPOSITION_TABLE = {
  "C":       0,
  "concert": 0,
  "Bb":      2,   // clarinet, trumpet, tenor sax, soprano sax
  "Eb":      9,   // alto sax, bari sax, Eb clarinet
  "F":       7,   // French horn, English horn
  "A":       3,   // clarinet in A
  "G":       5,   // alto flute
  "D":      -2,   // trumpet in D
};

// Major-key signatures: name -> { accidental: "#"|"b"|"", count: 0..7 }
const KEY_SIGNATURES = {
  "C":  { accidental: "",  count: 0 },
  "G":  { accidental: "#", count: 1 },
  "D":  { accidental: "#", count: 2 },
  "A":  { accidental: "#", count: 3 },
  "E":  { accidental: "#", count: 4 },
  "B":  { accidental: "#", count: 5 },
  "F#": { accidental: "#", count: 6 },
  "C#": { accidental: "#", count: 7 },
  "F":  { accidental: "b", count: 1 },
  "Bb": { accidental: "b", count: 2 },
  "Eb": { accidental: "b", count: 3 },
  "Ab": { accidental: "b", count: 4 },
  "Db": { accidental: "b", count: 5 },
  "Gb": { accidental: "b", count: 6 },
  "Cb": { accidental: "b", count: 7 },
};

// Order of flats / sharps with their conventional staff positions
// (staffPos = 0 is bottom line, 8 is top line)
const FLAT_KEYSIG_ORDER = [
  { letter: "B", treble: 4, bass: 2 },  // Bb
  { letter: "E", treble: 7, bass: 5 },  // Eb
  { letter: "A", treble: 3, bass: 1 },  // Ab
  { letter: "D", treble: 6, bass: 4 },  // Db
  { letter: "G", treble: 2, bass: 0 },  // Gb
  { letter: "C", treble: 5, bass: 3 },  // Cb
  { letter: "F", treble: 1, bass: -1 }, // Fb
];
const SHARP_KEYSIG_ORDER = [
  { letter: "F", treble: 8, bass: 6 },  // F#
  { letter: "C", treble: 5, bass: 3 },  // C#
  { letter: "G", treble: 9, bass: 7 },  // G#
  { letter: "D", treble: 6, bass: 4 },  // D#
  { letter: "A", treble: 3, bass: 1 },  // A#
  { letter: "E", treble: 7, bass: 5 },  // E#
  { letter: "B", treble: 4, bass: 2 },  // B#
];

// Pitch regex — compatible with synth.js format (~Eb3, ~F#4+50, ~A4-25)
const PITCH_RE = /^~?([A-Ga-g])([#b]?)(-?\d+)([+-]\d+)?$/;
const MIDI_RE = /^\d+(\.\d+)?$/;

// ============================================================
// SMuFL codepoints (Bravura font)
// ============================================================

const SMUFL = {
  trebleClef:   "\uE050",   // G clef
  bassClef:     "\uE062",   // F clef
  noteheadBlack:"\uE0A4",   // filled notehead (default)
  sharp:        "\uE262",
  flat:         "\uE260",
  natural:      "\uE261",
  doubleSharp:  "\uE263",
  doubleFlat:   "\uE264",
};

// The notation editor is on (notationEdit.js, the score's): a click on a
// note is for editing it, not for sounding it.
let editing = false;
export const isNotationEditing = () => editing;
export function setNotationEditing(on) { editing = !!on; }

// Curated notehead palette (style name -> SMuFL codepoint).
// Used by createNotation's noteheadStyles[] override and the edit-mode palette.
export const NOTEHEAD_STYLES = {
  filled:      "\uE0A4", // noteheadBlack       (default \u2014 quarter / eighth)
  half:        "\uE0A3", // noteheadHalf        (open)
  whole:       "\uE0A2", // noteheadWhole
  x:           "\uE0A9", // noteheadXBlack      (percussive / unpitched)
  diamond:     "\uE0D2", // noteheadDiamondBlack (artificial harmonic)
  diamondOpen: "\uE0DE", // noteheadDiamondHalf  (natural harmonic / open)
  square:      "\uE0B8", // noteheadSquareBlack
  slash:       "\uE0CB", // noteheadSlashedBlack1
};

// Articulation marks (style name -> { above, below } SMuFL codepoints).
// Rendered above the staff at each anchored note, stacking vertically when
// multiple marks share a note. The `below` form is used when a future stem
// direction selector calls for it; for now we render above by default.
export const ARTICULATION_STYLES = {
  staccato:        { above: "\uE4A2", below: "\uE4A3" }, // articStaccatoAbove
  staccatissimo:   { above: "\uE4A6", below: "\uE4A7" }, // articStaccatissimoAbove (wedge)
  accent:          { above: "\uE4A0", below: "\uE4A1" }, // articAccentAbove
  accentStaccato:  { above: "\uE4B0", below: "\uE4B1" }, // articAccentStaccatoAbove
  tenuto:          { above: "\uE4A4", below: "\uE4A5" }, // articTenutoAbove
  portato:         { above: "\uE4B2", below: "\uE4B3" }, // articTenutoStaccatoAbove (portato)
  tenutoAccent:    { above: "\uE4B4", below: "\uE4B5" }, // articTenutoAccentAbove
  marcato:         { above: "\uE4AC", below: "\uE4AD" }, // articMarcatoAbove
  marcatoStaccato: { above: "\uE4AE", below: "\uE4AF" }, // articMarcatoStaccatoAbove
  stress:          { above: "\uE4B6", below: "\uE4B7" }, // articStressAbove
  unstress:        { above: "\uE4B8", below: "\uE4B9" }, // articUnstressAbove
  laissezVibrer:   { above: "\uE4BA", below: "\uE4BB" }, // articLaissezVibrerAbove
  fermata:         { above: "\uE4C0", below: "\uE4C1" }, // fermataAbove
  fermataShort:    { above: "\uE4C4", below: "\uE4C5" }, // fermataShortAbove
  fermataLong:     { above: "\uE4C6", below: "\uE4C7" }, // fermataLongAbove
  fermataVeryLong: { above: "\uE4C8", below: "\uE4C9" }, // fermataVeryLongAbove
  harmonic:        { above: "\uE614", below: "\uE614" }, // stringsHarmonic
  snapPizz:        { above: "\uE631", below: "\uE631" }, // pluckedSnapPizzicatoAbove
};

// Dynamic marks (label -> SMuFL codepoint). Rendered below the staff at each
// anchored note. The SMuFL dynamics glyphs are designed to align on a common
// baseline regardless of letter mix, so they look correct as a row.
export const DYNAMIC_STYLES = {
  // Standard dynamics, soft \u2192 loud
  ppppp: "\uE528",
  pppp:  "\uE529",
  ppp:   "\uE52A",
  pp:    "\uE52B",
  p:     "\uE520",
  mp:    "\uE52C",
  mf:    "\uE52D",
  f:     "\uE522",
  ff:    "\uE52F",
  fff:   "\uE530",
  ffff:  "\uE531",
  fffff: "\uE532",
  // Compound / accent dynamics
  fp:    "\uE534",   // dynamicFortePiano
  pf:    "\uE52E",   // dynamicPF
  sf:    "\uE536",   // dynamicSforzando1
  sfp:   "\uE537",   // dynamicSforzandoPiano
  sfz:   "\uE539",   // dynamicSforzato
  sffz:  "\uE53C",   // dynamicSforzatoFF
  rf:    "\uE53D",   // dynamicRinforzando1
  rfz:   "\uE53E",   // dynamicRinforzando2
  fz:    "\uE535",   // dynamicForzando
  n:     "\uE526",   // dynamicNiente
};

// SMuFL font metrics (in staff spaces, 1 staff space = staffHeight/4)
// These position the glyph origins correctly relative to the staff.
const CLEF_METRICS = {
  // Treble clef: origin at G4 line (second line from bottom = staffPos 2)
  treble: { staffPos: 2, xOffset: 0.2 },
  // Bass clef: origin at F3 line (fourth line from bottom = staffPos 6)
  bass:   { staffPos: 6, xOffset: 0.2 },
};

// ============================================================
// Pitch parsing
// ============================================================

/**
 * Convert a parsed pitch to a (possibly fractional) MIDI number.
 * MIDI 60 = middle C (C4); cents are added as a fractional part.
 * @returns {number}
 */
export function pitchToMidi(pitch) {
  if (!pitch) return 0;
  let midi = (pitch.octave + 1) * 12 + (BASE_SEMI[pitch.noteLetter] ?? 0);
  if (pitch.accidental === "#") midi += 1;
  else if (pitch.accidental === "b") midi -= 1;
  if (pitch.cents) midi += pitch.cents / 100;
  return midi;
}

/**
 * Convert a (possibly fractional) MIDI number to frequency in Hz
 * using A4 = 440 Hz as the reference.
 */
export function midiToFreq(midi) {
  return 440 * Math.pow(2, (midi - 69) / 12);
}

/**
 * Format a parsed pitch as a human-readable name with proper accidental
 * symbols (♭, ♯) and an optional ¢ cents annotation. Used for tooltips.
 * @returns {string} e.g. "Eb3", "F♯4", "A4 -25¢"
 */
export function formatPitchName(pitch) {
  if (!pitch) return "";
  let s = pitch.noteLetter;
  if (pitch.accidental === "#")      s += "♯"; // ♯
  else if (pitch.accidental === "b") s += "♭"; // ♭
  s += pitch.octave;
  if (pitch.cents) {
    s += " " + (pitch.cents > 0 ? "+" : "") + pitch.cents + "¢"; // ¢
  }
  return s;
}

/**
 * Parse an Oscilla pitch string into components.
 * @param {string} pitchStr e.g. "~Eb3+50", "F#4", "~A4-25", or a MIDI number "60"
 * @returns {{ noteLetter: string, accidental: string, octave: number, cents: number } | null}
 */
export function parsePitch(pitchStr) {
  if (!pitchStr || typeof pitchStr !== "string") return null;
  // A MIDI note number (60 = middle C), spelled with sharps; a fraction is cents
  if (MIDI_RE.test(pitchStr.trim())) {
    const n = Number(pitchStr);
    if (n > 127) return null;
    const semis = Math.floor(n);
    const s = SHARP_SPELL[semis % 12];
    return {
      noteLetter: s.l,
      accidental: s.a,
      octave:     Math.floor(semis / 12) - 1,
      cents:      Math.round((n - semis) * 100),
    };
  }
  const m = pitchStr.trim().match(PITCH_RE);
  if (!m) return null;
  return {
    noteLetter:  m[1].toUpperCase(),
    accidental:  m[2] || "",
    octave:      Number(m[3]),
    cents:       m[4] ? Number(m[4]) : 0,
  };
}

// ============================================================
// Transposition
// ============================================================

/**
 * Look up the semitone offset for a named transposition or pass through a number.
 * @param {string|number} key
 * @returns {number}
 */
export function getTranspositionOffset(key) {
  if (typeof key === "number") return key;
  return TRANSPOSITION_TABLE[key] ?? 0;
}

/**
 * Transpose a parsed pitch by a number of semitones.
 * Cents are preserved (they represent a microtonal offset from the nearest semitone).
 * @param {{ noteLetter: string, accidental: string, octave: number, cents: number }} pitch
 * @param {number} semitones positive = up
 * @param {"#"|"b"|null} [preferAccidental] respelling preference for chromatic
 *   pitches (e.g. "b" if the surrounding key has flats). Defaults to sharps
 *   for upward transposition, flats for downward.
 * @returns {{ noteLetter: string, accidental: string, octave: number, cents: number }}
 */
export function transposePitch(pitch, semitones, preferAccidental = null) {
  let absSemi = pitch.octave * 12 + BASE_SEMI[pitch.noteLetter];
  if (pitch.accidental === "#") absSemi += 1;
  if (pitch.accidental === "b") absSemi -= 1;

  absSemi += semitones;

  const newOctave = Math.floor(absSemi / 12);
  const pc = ((absSemi % 12) + 12) % 12;
  const useFlats = preferAccidental
    ? preferAccidental === "b"
    : semitones < 0;
  const spell = useFlats ? FLAT_SPELL : SHARP_SPELL;
  const s = spell[pc];

  return {
    noteLetter: s.l,
    accidental: s.a,
    octave:     newOctave,
    cents:      pitch.cents,
  };
}

// ============================================================
// Staff position mapping
// ============================================================

/**
 * Convert a parsed pitch to a staff position integer.
 * 0 = bottom line, 8 = top line. Negative = below staff. >8 = above staff.
 * @param {{ noteLetter: string, octave: number }} pitch
 * @param {string} clef "treble" or "bass"
 * @returns {number}
 */
export function pitchToStaffPosition(pitch, clef) {
  const diatonic = pitch.octave * 7 + LETTER_INDEX[pitch.noteLetter];
  return diatonic - (CLEF_BOTTOM[clef] ?? CLEF_BOTTOM.treble);
}

// ============================================================
// SVG helpers
// ============================================================

function el(tag, attrs = {}) {
  const e = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
}

/** Create a Bravura text glyph at the given position */
function glyph(codepoint, x, y, fontSize, color) {
  const t = el("text", {
    x, y,
    "font-family": "Bravura, serif",
    "font-size": fontSize,
    fill: color,
    "pointer-events": "none",
  });
  t.textContent = codepoint;
  return t;
}

function drawStaffLines(parent, startY, staffHeight, totalWidth, color, lineWidth) {
  const g = el("g", { class: "notation-staff-lines" });
  const gap = staffHeight / 4;
  for (let i = 0; i < 5; i++) {
    const y = startY + i * gap;
    g.appendChild(el("line", {
      x1: 0, y1: y, x2: totalWidth, y2: y,
      stroke: color, "stroke-width": lineWidth,
    }));
  }
  parent.appendChild(g);
}

/**
 * Draw a major-key signature after the clef. Returns the x offset where
 * notes can start (i.e. the rightmost edge of the rendered key signature).
 */
function drawKeySignature(parent, keysigName, clef, startX, startY, staffHeight, color) {
  const keysig = KEY_SIGNATURES[keysigName];
  if (!keysig || keysig.count === 0) return startX;

  const fontSize = staffHeight;
  const halfSpace = staffHeight / 8;
  const order = keysig.accidental === "b" ? FLAT_KEYSIG_ORDER : SHARP_KEYSIG_ORDER;
  const cp = keysig.accidental === "b" ? SMUFL.flat : SMUFL.sharp;
  // Width per accidental in the signature is roughly 0.7 staff spaces.
  const stride = staffHeight / 4 * 0.7;

  let x = startX;
  for (let i = 0; i < keysig.count && i < order.length; i++) {
    const slot = order[i];
    const staffPos = clef === "bass" ? slot.bass : slot.treble;
    const y = startY + staffHeight - (staffPos * halfSpace);
    parent.appendChild(glyph(cp, x, y, fontSize, color));
    x += stride;
  }
  // breathing room before the first note
  return x + stride * 1.0;
}

function drawClef(parent, clef, startY, staffHeight, color) {
  const metrics = CLEF_METRICS[clef] || CLEF_METRICS.treble;
  const cp = clef === "bass" ? SMUFL.bassClef : SMUFL.trebleClef;
  const halfSpace = staffHeight / 8;

  // Font size = staffHeight (1em = 4 staff spaces in SMuFL)
  const fontSize = staffHeight;

  // Y position: the glyph origin sits on its reference line
  const y = startY + staffHeight - (metrics.staffPos * halfSpace);
  const x = metrics.xOffset * (staffHeight / 4);

  const clefEl = glyph(cp, x, y, fontSize, color);
  // Class lets edit-mode treat the clef as a dblclick target for the DSL editor.
  clefEl.classList.add("notation-clef");
  parent.appendChild(clefEl);
}

function drawNotehead(parent, cx, cy, staffHeight, color, style = "filled") {
  // Notehead glyph is centered on the staff position
  // SMuFL glyph origins are at the left edge, vertically on the staff line
  const fontSize = staffHeight;
  const spaceUnit = staffHeight / 4;
  const cp = NOTEHEAD_STYLES[style] || SMUFL.noteheadBlack;
  // Shift left by ~half notehead width so it's centered on cx
  const noteWidth = spaceUnit * 1.18;
  parent.appendChild(glyph(cp, cx - noteWidth / 2, cy, fontSize, color));
}

function drawAccidental(parent, type, x, cy, staffHeight, color) {
  const fontSize = staffHeight;
  let cp;
  if (type === "#")       cp = SMUFL.sharp;
  else if (type === "b")  cp = SMUFL.flat;
  else if (type === "n")  cp = SMUFL.natural;
  else if (type === "x")  cp = SMUFL.doubleSharp;
  else if (type === "bb") cp = SMUFL.doubleFlat;
  else return;

  parent.appendChild(glyph(cp, x, cy, fontSize, color));
}

function drawLedgerLines(parent, cx, staffPos, startY, staffHeight, noteRadius, color, lineWidth) {
  const halfSpace = staffHeight / 8;
  const hw = noteRadius * 1.6;

  // Below staff (positions < 0)
  if (staffPos < 0) {
    for (let pos = -2; pos >= staffPos; pos -= 2) {
      const y = startY + staffHeight - (pos * halfSpace);
      parent.appendChild(el("line", {
        x1: cx - hw, y1: y, x2: cx + hw, y2: y,
        stroke: color, "stroke-width": lineWidth,
        class: "notation-ledger",
      }));
    }
  }

  // Above staff (positions > 8)
  if (staffPos > 8) {
    for (let pos = 10; pos <= staffPos; pos += 2) {
      const y = startY + staffHeight - (pos * halfSpace);
      parent.appendChild(el("line", {
        x1: cx - hw, y1: y, x2: cx + hw, y2: y,
        stroke: color, "stroke-width": lineWidth,
        class: "notation-ledger",
      }));
    }
  }
}

/**
 * Render glisses (gliss / portamento / trill connection lines) inside a
 * dedicated <g class="notation-glisses"> sibling. Each entry is:
 *   { from: { index }, to: { index } | { x, y }, style?, id? }
 * Inserted into the parent BEFORE the notehead group so noteheads
 * always render on top.
 */
function drawGlisses(parent, glisses, notePositions, color, lineWidth, staffHeight) {
  if (!Array.isArray(glisses) || glisses.length === 0) return;
  const g = el("g", { class: "notation-glisses" });

  // Distance to back off from each connected notehead so the line starts /
  // ends just outside the glyph rather than colliding with it. Notehead
  // glyph half-width ≈ 0.59 staff-spaces (drawNotehead uses spaceUnit*1.18
  // / 2). Add a small breathing pad on top.
  const spaceUnit = staffHeight / 4;
  const edgeOffset = spaceUnit * 0.6 + spaceUnit * 0.15;

  for (const gl of glisses) {
    const fromIdx = gl?.from?.index;
    if (!Number.isFinite(fromIdx)) continue;
    const fp = notePositions[fromIdx];
    if (!fp) continue;

    // Build the raw point list: source + breakpoints + destination.
    // Breakpoints are in local notation coords.
    const points = [{ x: fp.cx, y: fp.cy }];
    if (Array.isArray(gl.breakpoints)) {
      for (const bp of gl.breakpoints) {
        if (Number.isFinite(bp?.x) && Number.isFinite(bp?.y)) {
          points.push({ x: bp.x, y: bp.y });
        }
      }
    }
    let toIsNote = false;
    if (Number.isFinite(gl?.to?.index)) {
      const tp = notePositions[gl.to.index];
      if (!tp) continue;
      points.push({ x: tp.cx, y: tp.cy });
      toIsNote = true;
    } else if (Number.isFinite(gl?.to?.x) && Number.isFinite(gl?.to?.y)) {
      points.push({ x: gl.to.x, y: gl.to.y });
    } else {
      continue;
    }
    if (points.length < 2) continue;

    // Offset the FIRST segment so the source end clears the source notehead.
    {
      const a = points[0];
      const b = points[1];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len >= 0.001) {
        points[0] = { x: a.x + (dx / len) * edgeOffset, y: a.y + (dy / len) * edgeOffset };
      }
    }
    // If destination is connected, offset the LAST segment so the line
    // ends before colliding with the destination notehead.
    if (toIsNote) {
      const i = points.length - 1;
      const a = points[i - 1];
      const b = points[i];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len >= 0.001) {
        points[i] = { x: b.x - (dx / len) * edgeOffset, y: b.y - (dy / len) * edgeOffset };
      }
    }

    // Bail out if the offsets shortened the first segment past itself
    // (very close notes with no breakpoints).
    if (points.length === 2) {
      const orig = { x: fp.cx, y: fp.cy };
      const sdx = points[1].x - points[0].x;
      const sdy = points[1].y - points[0].y;
      const odx = (Number.isFinite(gl?.to?.index) ? notePositions[gl.to.index].cx : gl.to.x) - orig.x;
      const ody = (Number.isFinite(gl?.to?.index) ? notePositions[gl.to.index].cy : gl.to.y) - orig.y;
      if (sdx * odx + sdy * ody <= 0) continue;
    }

    // Build the path d. Style modulates: a "wavy" or "slur" style on a
    // single-segment gliss replaces the straight line with a curve.
    // Polylines (with breakpoints) always use straight segments — per-
    // segment styling could come later.
    const style = gl.style || "straight";
    const baseStrokeWidth = Math.max(lineWidth * 1.4, 1);
    let d;
    let extraAttrs = {};

    if (points.length === 2 && style === "wavy") {
      d = wavePathBetween(points[0], points[1], spaceUnit);
    } else if (points.length === 2 && style === "slur") {
      // Quadratic bezier with control point offset perpendicular to the line
      const a = points[0], b = points[1];
      const dx = b.x - a.x, dy = b.y - a.y;
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      const px = -dy / len, py = dx / len;          // perpendicular unit
      const arc = Math.min(spaceUnit * 1.4, len * 0.25);
      const cx = (a.x + b.x) / 2 + px * arc;
      const cy = (a.y + b.y) / 2 + py * arc;
      d = `M ${a.x.toFixed(2)} ${a.y.toFixed(2)} Q ${cx.toFixed(2)} ${cy.toFixed(2)} ${b.x.toFixed(2)} ${b.y.toFixed(2)}`;
    } else {
      d = `M ${points[0].x.toFixed(2)} ${points[0].y.toFixed(2)}`;
      for (let i = 1; i < points.length; i++) {
        d += ` L ${points[i].x.toFixed(2)} ${points[i].y.toFixed(2)}`;
      }
    }

    if (style === "dotted") {
      extraAttrs["stroke-dasharray"] = `${baseStrokeWidth * 1.2} ${spaceUnit * 0.45}`;
    } else if (style === "dashed") {
      extraAttrs["stroke-dasharray"] = `${spaceUnit * 0.55} ${spaceUnit * 0.4}`;
    }

    const path = el("path", {
      d,
      stroke: color,
      "stroke-width": baseStrokeWidth,
      "stroke-linecap": "round",
      "stroke-linejoin": "round",
      fill: "none",
      class: `notation-gliss notation-gliss-${style}`,
      ...extraAttrs,
    });
    if (gl.id) path.setAttribute("data-gliss-id", String(gl.id));
    if (Number.isFinite(fromIdx)) path.setAttribute("data-from-index", String(fromIdx));
    if (Number.isFinite(gl?.to?.index)) path.setAttribute("data-to-index", String(gl.to.index));
    g.appendChild(path);

    // Style decorations layered on top of the base path
    const last = points[points.length - 1];
    const beforeLast = points[points.length - 2] || last;
    if (style === "arrowed") {
      const dxA = last.x - beforeLast.x;
      const dyA = last.y - beforeLast.y;
      const lenA = Math.sqrt(dxA * dxA + dyA * dyA) || 1;
      const ux = dxA / lenA, uy = dyA / lenA;
      const px = -uy, py = ux;
      const head = spaceUnit * 0.55;
      const baseX = last.x - ux * head;
      const baseY = last.y - uy * head;
      g.appendChild(el("path", {
        d: `M ${last.x.toFixed(2)} ${last.y.toFixed(2)} L ${(baseX + px * head * 0.6).toFixed(2)} ${(baseY + py * head * 0.6).toFixed(2)} L ${(baseX - px * head * 0.6).toFixed(2)} ${(baseY - py * head * 0.6).toFixed(2)} Z`,
        fill: color,
        stroke: color,
        "stroke-width": baseStrokeWidth,
        class: "notation-gliss-arrow-head",
      }));
    } else if (style === "trill") {
      // 'tr' italic glyph at the source end
      const a = points[0];
      const tr = el("text", {
        x: a.x - spaceUnit * 0.3,
        y: a.y - spaceUnit * 0.3,
        "font-family": "Bravura, serif",
        "font-style": "italic",
        "font-size": spaceUnit * 2.2,
        fill: color,
        "pointer-events": "none",
        class: "notation-gliss-trill-glyph",
      });
      tr.textContent = "tr";
      g.appendChild(tr);
    }
  }

  parent.appendChild(g);
}

/**
 * Build a sine-wave path d-string from point a to point b, with the wave
 * perpendicular to the line direction. Used by drawGlisses for wavy
 * style glisses.
 */
function wavePathBetween(a, b, period) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len < 0.001) return `M ${a.x} ${a.y} L ${b.x} ${b.y}`;
  const ux = dx / len, uy = dy / len;
  const px = -uy, py = ux;
  const amp = period * 0.4;
  const cycles = Math.max(1, Math.round(len / period));
  const samples = cycles * 10;
  let d = `M ${a.x.toFixed(2)} ${a.y.toFixed(2)}`;
  for (let i = 1; i <= samples; i++) {
    const t = i / samples;
    const wave = Math.sin(t * cycles * 2 * Math.PI) * amp;
    const x = a.x + t * dx + px * wave;
    const y = a.y + t * dy + py * wave;
    d += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
  }
  return d;
}


/**
 * Draw a horizontal extent bar from x1 to x2, vertically centred on the
 * staff. Used to indicate a held / sustained gesture extending past the
 * notes — Lutosławski / Penderecki box-notation style.
 */
function drawExtent(parent, x1, x2, startY, staffHeight, style, color, lineWidth) {
  if (x2 <= x1) return;
  const g = el("g", { class: "notation-extent" });
  const yMid = startY + staffHeight / 2;
  const spaceUnit = staffHeight / 4;

  switch (style) {
    case "thin": {
      g.appendChild(el("line", {
        x1, y1: yMid, x2, y2: yMid,
        stroke: color,
        "stroke-width": Math.max(lineWidth * 1.5, 1),
        class: "notation-extent-thin",
      }));
      break;
    }

    case "dashed": {
      g.appendChild(el("line", {
        x1, y1: yMid, x2, y2: yMid,
        stroke: color,
        "stroke-width": Math.max(lineWidth * 1.5, 1),
        "stroke-dasharray": `${spaceUnit * 0.6} ${spaceUnit * 0.4}`,
        class: "notation-extent-dashed",
      }));
      break;
    }

    case "wavy": {
      // Sine-wiggle path. Period = ~1 staff space; amplitude = ~0.4 spaces.
      const period = spaceUnit;
      const amp = spaceUnit * 0.4;
      const len = x2 - x1;
      const cycles = Math.max(1, Math.round(len / period));
      const samplesPerCycle = 8;
      const samples = cycles * samplesPerCycle;
      let d = `M ${x1} ${yMid}`;
      for (let i = 1; i <= samples; i++) {
        const t = i / samples;
        const x = x1 + t * len;
        const y = yMid + amp * Math.sin(t * cycles * 2 * Math.PI);
        d += ` L ${x.toFixed(2)} ${y.toFixed(2)}`;
      }
      g.appendChild(el("path", {
        d,
        stroke: color,
        fill: "none",
        "stroke-width": Math.max(lineWidth * 1.4, 1),
        "stroke-linecap": "round",
        class: "notation-extent-wavy",
      }));
      break;
    }

    case "dottedTrill": {
      // tr glyph followed by a dotted line at the staff midline
      const trEl = el("text", {
        x: x1 + spaceUnit * 0.1,
        y: yMid - spaceUnit * 0.1,
        "font-family": "Bravura, serif",
        "font-style": "italic",
        "font-size": staffHeight * 0.6,
        fill: color,
        "pointer-events": "none",
      });
      trEl.textContent = "tr";
      g.appendChild(trEl);
      const dotStartX = x1 + spaceUnit * 1.6;
      if (dotStartX < x2) {
        g.appendChild(el("line", {
          x1: dotStartX, y1: yMid, x2, y2: yMid,
          stroke: color,
          "stroke-width": Math.max(lineWidth * 1.5, 1),
          "stroke-dasharray": `${lineWidth * 1.5} ${spaceUnit * 0.4}`,
          "stroke-linecap": "round",
          class: "notation-extent-trill-dots",
        }));
      }
      break;
    }

    case "arrowed": {
      const arrowSize = spaceUnit * 0.5;
      g.appendChild(el("line", {
        x1, y1: yMid, x2: x2 - arrowSize, y2: yMid,
        stroke: color,
        "stroke-width": Math.max(lineWidth * 1.5, 1),
        class: "notation-extent-arrow-shaft",
      }));
      // Arrowhead via a small triangle path
      const ah = `M ${x2} ${yMid} L ${x2 - arrowSize} ${yMid - arrowSize * 0.6} L ${x2 - arrowSize} ${yMid + arrowSize * 0.6} Z`;
      g.appendChild(el("path", {
        d: ah,
        fill: color,
        stroke: color,
        "stroke-width": lineWidth,
        class: "notation-extent-arrow-head",
      }));
      break;
    }

    case "block":
    default: {
      const h = staffHeight * 0.30;
      g.appendChild(el("rect", {
        x: x1,
        y: yMid - h / 2,
        width: x2 - x1,
        height: h,
        fill: color,
        stroke: color,
        "stroke-width": lineWidth,
        class: "notation-extent-block",
      }));
      break;
    }
  }

  parent.appendChild(g);
}

function drawCentsLabel(parent, cx, cy, cents, noteRadius, color) {
  const label = (cents > 0 ? "+" : "") + cents;
  const fontSize = noteRadius * 1.4;
  const t = el("text", {
    x: cx + noteRadius * 1.6,
    y: cy - noteRadius * 0.6,
    "font-size": fontSize,
    "font-family": "monospace, sans-serif",
    fill: color,
    opacity: 0.7,
    "pointer-events": "none",
    class: "notation-cents",
  });
  t.textContent = label;
  parent.appendChild(t);
}

// ============================================================
// Main export
// ============================================================

/**
 * Multi-stave wrapper for piano / organ / string quartet etc.
 *
 * Renders N stacked staves inside a single <g class="oscilla-notation
 * oscilla-notation-system">. Each stave is rendered via createNotation()
 * (the single-stave function below), translated vertically by a running
 * y-offset. Cross-stave glisses (whose from / to references include a
 * staveIdx) are drawn in the outer wrapper after all staves are placed,
 * using world coordinates inside the system.
 *
 * @param {Object}   sysOptions
 * @param {Object[]} sysOptions.staves        per-stave option overrides
 * @param {number}   [sysOptions.staveGap=30] vertical gap between staves
 * @param {Object[]} [sysOptions.glisses]     glisses possibly spanning staves
 * @param {Object}   ...sharedOptions         passed to every createNotation call
 *                                             unless overridden in the stave's
 *                                             entry (clef, transp, keysig,
 *                                             staffHeight, etc.).
 * @returns {SVGGElement}
 */
export function createNotationSystem(sysOptions = {}) {
  const {
    staves = [],
    staveGap = 30,
    glisses = null,
    // System-level extent + box. When these are set they take over from
    // any per-stave extentX / boxed setting: ONE shared bar across all
    // staves (drawn at each stave's middle) and ONE box around the whole
    // system. This matches the user expectation that piano / organ /
    // quartet read as a single object, not per-stave fragments.
    extentX: systemExtentX = null,
    extentStyle: systemExtentStyle = "block",
    boxed: systemBoxed = false,
    boxRadius: systemBoxRadius = 0,
    boxPadding: systemBoxPadding = 6,
    ...shared
  } = sysOptions;

  const root = el("g", { class: "oscilla-notation oscilla-notation-system" });

  // Render each stave; track its yOffset and the per-note positions so
  // cross-stave glisses can refer to them.
  const staveInfos = [];
  let yCursor = 0;
  staves.forEach((staveOpts, sIdx) => {
    const merged = { ...shared, ...staveOpts, startY: 0 };
    // We disable cross-stave glisses inside the per-stave call; the outer
    // wrapper handles all glisses (both single-stave and cross-stave).
    merged.glisses = null;
    // System-level extent / box take precedence over per-stave settings —
    // mute them at the per-stave level so we only draw one of each.
    if (systemExtentX != null) merged.extentX = null;
    if (systemBoxed)           merged.boxed   = false;
    const staveG = createNotation(staveOpts.pitches || [], merged);
    staveG.setAttribute("transform", `translate(0, ${yCursor})`);
    staveG.classList.add("oscilla-notation-stave");
    staveG.setAttribute("data-stave-index", String(sIdx));
    root.appendChild(staveG);
    staveInfos.push({
      g: staveG,
      yOffset: yCursor,
      staffHeight: merged.staffHeight ?? 40,
      pitches: staveOpts.pitches || [],
      transposition: merged.transposition || "C",
      staffEnd: staveG._staffEnd,
    });
    yCursor += (merged.staffHeight ?? 40) + staveGap;
  });

  // ── System-level extent: one bar per stave at the same shared x, all
  //    starting from each stave's right edge. Uses the same drawExtent
  //    helper as single-stave so visual styles match exactly.
  if (systemExtentX != null && staveInfos.length) {
    const color = shared.color ?? "#000";
    const extentG = el("g", { class: "notation-system-extent" });
    for (const si of staveInfos) {
      // Each bar starts where its own stave's lines end; a stave already
      // longer than the extent gets none (as a single stave does).
      const xStart = si.staffEnd;
      if (!(systemExtentX > xStart)) continue;
      // drawExtent renders into a sub-<g>; offset by yOffset so it sits
      // at THIS stave's vertical position.
      const cell = el("g", { transform: `translate(0, ${si.yOffset})` });
      drawExtent(cell, xStart, systemExtentX, 0, si.staffHeight, systemExtentStyle, color, shared.staffLineWidth ?? 0.8);
      extentG.appendChild(cell);
    }
    root.appendChild(extentG);
  }

  // ── System-level bounding box: ONE rect around all staves.
  if (systemBoxed && staveInfos.length) {
    const color = shared.color ?? "#000";
    const lineWidth = shared.staffLineWidth ?? 0.8;
    const last = staveInfos[staveInfos.length - 1];
    const top = staveInfos[0].yOffset;
    const bottom = last.yOffset + last.staffHeight;
    // Right edge: max of (extent end, every stave's right edge)
    let right = systemExtentX != null ? systemExtentX : 0;
    for (const si of staveInfos) right = Math.max(right, si.staffEnd);
    const pad = systemBoxPadding;
    const box = el("rect", {
      x: -pad,
      y: top - pad,
      width: right + pad * 2,
      height: (bottom - top) + pad * 2,
      rx: systemBoxRadius || 0,
      ry: systemBoxRadius || 0,
      fill: "none",
      stroke: color,
      "stroke-width": lineWidth,
      class: "notation-system-box",
    });
    // Insert box BEHIND staves so noteheads stay clickable. Prepend to root.
    root.insertBefore(box, root.firstChild);
  }

  // Cross-stave glisses (and intra-stave ones if the cue's data lives at
  // the system level rather than per-stave). Each gliss endpoint can be:
  //   { staveIdx, index }                — connected to a notehead
  //   { x, y }                            — free-floating (in system coords)
  if (Array.isArray(glisses) && glisses.length) {
    const lineWidth = shared.staffLineWidth ?? 0.8;
    const color = shared.color ?? "#000";
    const refStaffHeight = shared.staffHeight ?? 40;

    // Build a lookup: (staveIdx, noteIdx) → { cx, cy } in system coords.
    const noteWorld = new Map();
    staveInfos.forEach((si, sIdx) => {
      const noteEls = si.g.querySelectorAll(".notation-note");
      noteEls.forEach((nel, i) => {
        const cx = parseFloat(nel.getAttribute("data-cx"));
        const cy = parseFloat(nel.getAttribute("data-cy"));
        if (Number.isFinite(cx) && Number.isFinite(cy)) {
          noteWorld.set(`${sIdx}::${i}`, { cx, cy: cy + si.yOffset });
        }
      });
    });

    // Same offset-and-style logic as drawGlisses, but endpoints are keyed
    // by (staveIdx, noteIdx) in system coords.
    const sysG = el("g", { class: "notation-glisses notation-glisses-system" });
    const spaceUnit = refStaffHeight / 4;
    const edgeOffset = spaceUnit * 0.6 + spaceUnit * 0.15;

    glisses.forEach((gl) => {
      const fromKey = (gl?.from && Number.isFinite(gl.from.staveIdx) && Number.isFinite(gl.from.index))
        ? `${gl.from.staveIdx}::${gl.from.index}`
        : null;
      if (!fromKey) return;
      const fp = noteWorld.get(fromKey);
      if (!fp) return;

      let tx, ty, toIsNote = false;
      if (gl?.to && Number.isFinite(gl.to.staveIdx) && Number.isFinite(gl.to.index)) {
        const tKey = `${gl.to.staveIdx}::${gl.to.index}`;
        const tp = noteWorld.get(tKey);
        if (!tp) return;
        tx = tp.cx; ty = tp.cy; toIsNote = true;
      } else if (Number.isFinite(gl?.to?.x) && Number.isFinite(gl?.to?.y)) {
        tx = gl.to.x; ty = gl.to.y;
      } else {
        return;
      }

      // Build polyline through breakpoints (system coords)
      const pts = [{ x: fp.cx, y: fp.cy }];
      if (Array.isArray(gl.breakpoints)) {
        for (const bp of gl.breakpoints) {
          if (Number.isFinite(bp?.x) && Number.isFinite(bp?.y)) pts.push({ x: bp.x, y: bp.y });
        }
      }
      pts.push({ x: tx, y: ty });

      // Apply notehead-edge offsets to first / last segment
      {
        const a = pts[0], b = pts[1];
        const dx = b.x - a.x, dy = b.y - a.y;
        const L = Math.sqrt(dx * dx + dy * dy);
        if (L >= 0.001) pts[0] = { x: a.x + (dx / L) * edgeOffset, y: a.y + (dy / L) * edgeOffset };
      }
      if (toIsNote) {
        const i = pts.length - 1;
        const a = pts[i - 1], b = pts[i];
        const dx = b.x - a.x, dy = b.y - a.y;
        const L = Math.sqrt(dx * dx + dy * dy);
        if (L >= 0.001) pts[i] = { x: b.x - (dx / L) * edgeOffset, y: b.y - (dy / L) * edgeOffset };
      }

      let d = `M ${pts[0].x.toFixed(2)} ${pts[0].y.toFixed(2)}`;
      for (let i = 1; i < pts.length; i++) {
        d += ` L ${pts[i].x.toFixed(2)} ${pts[i].y.toFixed(2)}`;
      }

      const path = el("path", {
        d,
        stroke: color,
        "stroke-width": Math.max(lineWidth * 1.4, 1),
        "stroke-linecap": "round",
        "stroke-linejoin": "round",
        fill: "none",
        class: `notation-gliss notation-gliss-${gl.style || "straight"}`,
      });
      if (gl.id) path.setAttribute("data-gliss-id", String(gl.id));
      if (gl.from) {
        path.setAttribute("data-from-stave", String(gl.from.staveIdx));
        path.setAttribute("data-from-index", String(gl.from.index));
      }
      if (gl.to && Number.isFinite(gl.to.staveIdx)) {
        path.setAttribute("data-to-stave", String(gl.to.staveIdx));
        path.setAttribute("data-to-index", String(gl.to.index));
      }
      // Style variants reuse the dasharray patterns from drawExtent
      const style = gl.style || "straight";
      const baseStrokeWidth = Math.max(lineWidth * 1.4, 1);
      if (style === "dotted") path.setAttribute("stroke-dasharray", `${baseStrokeWidth * 1.2} ${spaceUnit * 0.45}`);
      if (style === "dashed") path.setAttribute("stroke-dasharray", `${spaceUnit * 0.55} ${spaceUnit * 0.4}`);
      sysG.appendChild(path);
    });
    root.appendChild(sysG);
  }

  return root;
}

/**
 * Create an SVG <g> group containing a staff fragment with noteheads.
 *
 * @param {string[]} pitches  Pitch strings: "~Eb3", "~F4+50", "~A#4-25"
 * @param {Object}   [options]
 * @param {string|number} [options.transposition="C"]  Instrument key or semitone offset
 * @param {string}  [options.clef="treble"]       "treble" or "bass"
 * @param {string}  [options.keysig=""]           Major key for key signature ("Eb", "G", "F#"…)
 * @param {number}  [options.staffHeight=40]      SVG units for the 5-line staff
 * @param {number}  [options.noteSpacing=18]      Horizontal gap between notes
 * @param {number}  [options.noteRadius=5]        Notehead ellipse radius (for ledger line width)
 * @param {number}  [options.startX=14]           X offset (room for clef + key sig prepended)
 * @param {number}  [options.startY=0]            Y offset for top staff line
 * @param {boolean} [options.showClef=true]        Render clef
 * @param {boolean} [options.showStaffLines=true]  Render 5 staff lines
 * @param {string}  [options.color="#000"]         Stroke/fill colour
 * @param {number}  [options.staffLineWidth=0.8]   Staff line stroke width
 * @returns {SVGGElement}
 */
export function createNotation(pitches, options = {}) {
  const {
    transposition  = "C",
    clef           = "treble",
    keysig         = "",
    staffHeight    = 40,
    noteSpacing    = 18,
    noteRadius     = 5,
    startX         = 14,
    startY         = 0,
    showClef       = true,
    showStaffLines = true,
    color          = "#000",
    staffLineWidth = 0.8,
    // Edit-mode overrides:
    noteOffsets    = null,  // array of additional x offsets per note (positions are still computed from noteSpacing; offset adds on top)
    staffWidth     = null,  // override total staff line width (extends/contracts the 5 lines)
    noteheadStyles = null,  // array of style names per note
    extentX        = null,  // x at which the extent line ends (local coords); null = no extent line
    extentStyle    = "block", // "block" | "thin" | "dashed" | "wavy" | "dottedTrill" | "arrowed"
    boxed          = false, // true → draw a rectangle outline around the whole fragment
    boxRadius      = 0,     // optional rounded corners on the bounding box
    boxPadding     = 6,     // padding from staff edges to the box
    glisses        = null,  // array of { from:{index}, to:{index}|{x,y}, style? }
    articulations  = null,  // sparse map { noteIdx: ["staccato","accent",...] }
    dynamics       = null,  // sparse map { noteIdx: "mf" }
  } = options;

  const halfSpace = staffHeight / 8;
  const offset = getTranspositionOffset(transposition);

  // Use the key signature (if any) to bias enharmonic spelling so e.g.
  // an A# in a flat key gets respelled as Bb.
  const ksInfo = KEY_SIGNATURES[keysig];
  const preferAccidental = ksInfo && ksInfo.accidental ? ksInfo.accidental : null;

  // Parse the input pitches (these are the sounding/concert pitches).
  // `parsed` is the written pitch list (sounding + transposition offset)
  // used for staff layout. `original` is kept around to populate hover
  // tooltips with the sounding name.
  const original = pitches.map(p => parsePitch(p)).filter(p => p !== null);
  const parsed = offset !== 0
    ? original.map(p => transposePitch(p, offset, preferAccidental))
    : original;

  // Reserve space for the clef. The clef glyph is roughly 2 staff-spaces wide.
  const clefWidth = showClef ? staffHeight * 0.7 : 0;
  // Reserve space for the key signature. Each accidental is ~0.7 staff-spaces.
  const ksAccCount = ksInfo ? ksInfo.count : 0;
  const ksWidth = ksAccCount > 0 ? (staffHeight / 4) * 0.7 * (ksAccCount + 1.0) : 0;
  const notesStartX = startX + clefWidth + ksWidth;

  // Total width — staff lines may be extended via the staffWidth override
  // (used by notation edit mode's right-edge handle); note layout always
  // uses the natural width derived from spacing + per-note offsets.
  const naturalWidth = notesStartX + parsed.length * noteSpacing + noteSpacing * 0.5;
  const lineWidth = staffWidth != null ? Math.max(staffWidth, naturalWidth * 0.4) : naturalWidth;

  // Root group
  const root = el("g", { class: "oscilla-notation" });

  // Staff lines
  if (showStaffLines) {
    drawStaffLines(root, startY, staffHeight, lineWidth, color, staffLineWidth);
  }

  // Extent bar — a horizontal indicator stretching past the end of the
  // staff lines. Used for sustained-note / box-style notation
  // (Lutosławski / Penderecki).
  if (extentX != null && extentX > lineWidth) {
    drawExtent(root, lineWidth, extentX, startY, staffHeight, extentStyle, color, staffLineWidth);
  }

  // Bounding box around the whole fragment. Inserted as the FIRST child
  // so it sits BEHIND notes/lines/extent. Encompasses the staff lines
  // plus the extent (if any), with vertical padding sufficient for one
  // ledger line above/below the staff.
  if (boxed) {
    const rightEdge = Math.max(lineWidth, extentX || 0);
    const ledgerPad = staffHeight * 0.5;
    const bx = 0 - boxPadding;
    const by = startY - ledgerPad - boxPadding;
    const bw = rightEdge + boxPadding * 2;
    const bh = staffHeight + ledgerPad * 2 + boxPadding * 2;
    const boxAttrs = {
      x: bx,
      y: by,
      width: bw,
      height: bh,
      fill: "none",
      stroke: color,
      "stroke-width": Math.max(staffLineWidth * 1.5, 1),
      class: "notation-bounding-box",
    };
    if (boxRadius > 0) { boxAttrs.rx = boxRadius; boxAttrs.ry = boxRadius; }
    const boxEl = el("rect", boxAttrs);
    root.insertBefore(boxEl, root.firstChild);
  }

  // Clef
  if (showClef) {
    drawClef(root, clef, startY, staffHeight, color);
  }

  // Key signature
  if (ksAccCount > 0) {
    drawKeySignature(root, keysig, clef, startX + clefWidth, startY, staffHeight, color);
  }

  // Note letters that are altered by the key signature — used to suppress
  // redundant accidentals on the noteheads.
  const ksAlteredLetters = new Set();
  if (ksInfo && ksAccCount > 0) {
    const order = ksInfo.accidental === "b" ? FLAT_KEYSIG_ORDER : SHARP_KEYSIG_ORDER;
    for (let i = 0; i < ksAccCount && i < order.length; i++) {
      ksAlteredLetters.add(order[i].letter);
    }
  }

  // Pre-compute per-note (cx, cy) so glisses can refer to source/target
  // noteheads by index without duplicating the layout logic.
  const notePositions = parsed.map((pitch, i) => {
    const staffPos = pitchToStaffPosition(pitch, clef);
    const userOffset = (noteOffsets && Number.isFinite(noteOffsets[i])) ? noteOffsets[i] : 0;
    const cx = notesStartX + (i + 0.5) * noteSpacing + userOffset;
    const cy = startY + staffHeight - (staffPos * halfSpace);
    return { cx, cy, staffPos };
  });

  // Glisses. Inserted BEFORE notes so
  // noteheads render on top of the line endings.
  if (glisses && glisses.length) {
    drawGlisses(root, glisses, notePositions, color, staffLineWidth, staffHeight);
  }

  // Notes
  const notesG = el("g", { class: "notation-noteheads" });
  const spaceUnit = staffHeight / 4;

  parsed.forEach((pitch, i) => {
    const noteG = el("g", { class: "notation-note", "data-index": i });

    // Hover tooltip showing the SOUNDING pitch (the original input). When a
    // transposition is active, also show the written name in parentheses.
    // pointer-events:visiblePainted overrides the wrapper's pointer-events:
    // none so hover registers on the glyph; the staff-line / empty-staff
    // area still passes through to the host rect for drag.
    noteG.style.pointerEvents = "visiblePainted";
    const titleEl = el("title");
    const sounding = formatPitchName(original[i]);
    const written = offset !== 0 ? formatPitchName(pitch) : null;
    titleEl.textContent = written
      ? `${sounding}  (written ${written})`
      : sounding;
    noteG.appendChild(titleEl);

    const { cx, cy, staffPos } = notePositions[i];
    noteG.setAttribute("data-note-index", String(i));
    noteG.setAttribute("data-cx", String(cx));
    noteG.setAttribute("data-cy", String(cy));

    // Ledger lines (behind notehead)
    drawLedgerLines(noteG, cx, staffPos, startY, staffHeight, noteRadius, color, staffLineWidth);

    // Accidental — positioned left of notehead. Skip if the key signature
    // already alters this letter in the same direction (so the player
    // isn't seeing both the key sig and a redundant accidental).
    if (pitch.accidental) {
      const ksMatches = ksInfo &&
        ksAlteredLetters.has(pitch.noteLetter) &&
        ksInfo.accidental === pitch.accidental;
      if (!ksMatches) {
        const accX = cx - spaceUnit * 1.4;
        drawAccidental(noteG, pitch.accidental, accX, cy, staffHeight, color);
      }
    } else if (ksInfo && ksAlteredLetters.has(pitch.noteLetter)) {
      // The key sig says this letter is altered, but this pitch has no
      // accidental → render a natural sign so the player doesn't apply
      // the key sig automatically.
      const accX = cx - spaceUnit * 1.4;
      drawAccidental(noteG, "n", accX, cy, staffHeight, color);
    }

    // Notehead
    const style = (noteheadStyles && typeof noteheadStyles[i] === "string")
      ? noteheadStyles[i]
      : "filled";
    drawNotehead(noteG, cx, cy, staffHeight, color, style);

    // Cents label
    if (pitch.cents !== 0) {
      drawCentsLabel(noteG, cx, cy, pitch.cents, noteRadius, color);
    }

    // Invisible hit-target rect over the notehead's bbox. Without this the
    // <text> glyph only catches clicks on its actually-painted pixels
    // (`pointer-events: visiblePainted`), so off-by-a-few-px clicks fall
    // through to whatever's behind. The rect is the LAST child so it
    // paints on top of the glyph and intercepts every pointer event in
    // its bounding rectangle.
    const hitW = staffHeight * 0.55;
    const hitH = staffHeight * 0.42;
    const hit = el("rect", {
      x: (cx - hitW / 2).toFixed(2),
      y: (cy - hitH / 2).toFixed(2),
      width: hitW.toFixed(2),
      height: hitH.toFixed(2),
      fill: "transparent",
      class: "notation-note-hit",
    });
    hit.style.pointerEvents = "all";
    hit.style.cursor = "pointer";
    noteG.appendChild(hit);

    notesG.appendChild(noteG);
  });

  root.appendChild(notesG);

  // ── Articulations (above each anchored note) ────────────────────────
  // Draws stacked SMuFL marks above the highest staff line. Multiple marks
  // on one note stack vertically with a small gap between them. The first
  // mark sits one staff space above the top staff line; the staccato dot
  // is dropped a little since it would otherwise float too high.
  if (articulations && typeof articulations === "object") {
    const aG = el("g", { class: "notation-articulations" });
    aG.style.pointerEvents = "none";
    const spaceUnit2 = staffHeight / 4;
    for (const [keyStr, marks] of Object.entries(articulations)) {
      if (!Array.isArray(marks) || marks.length === 0) continue;
      const i = Number(keyStr);
      if (!Number.isInteger(i) || i < 0 || i >= notePositions.length) continue;
      const { cx } = notePositions[i];
      let yCursor = startY - spaceUnit2 * 0.6;     // above the top staff line
      const stackGap = spaceUnit2 * 0.9;
      for (const mark of marks) {
        const def = ARTICULATION_STYLES[mark];
        if (!def) continue;
        const t = el("text", {
          x: cx, y: yCursor,
          "font-family": "Bravura, serif",
          "font-size": staffHeight * 0.55,
          fill: color,
          "text-anchor": "middle",
          class: `notation-articulation notation-articulation-${mark}`,
          "data-note-index": String(i),
          "data-mark": mark,
        });
        t.textContent = def.above;
        aG.appendChild(t);
        yCursor -= stackGap;
      }
    }
    root.appendChild(aG);
  }

  // ── Dynamics (below the staff at each anchored note) ────────────────
  if (dynamics && typeof dynamics === "object") {
    const dG = el("g", { class: "notation-dynamics" });
    dG.style.pointerEvents = "none";
    const yEnd = startY + staffHeight;
    const yDyn = yEnd + (staffHeight / 4) * 1.6;   // ~1.6 staff spaces below
    for (const [keyStr, label] of Object.entries(dynamics)) {
      if (!label) continue;
      const i = Number(keyStr);
      if (!Number.isInteger(i) || i < 0 || i >= notePositions.length) continue;
      const glyph = DYNAMIC_STYLES[label];
      if (!glyph) continue;
      const { cx } = notePositions[i];
      const t = el("text", {
        x: cx, y: yDyn,
        "font-family": "Bravura, serif",
        "font-size": staffHeight * 0.7,
        "font-style": "italic",
        fill: color,
        "text-anchor": "middle",
        class: `notation-dynamic notation-dynamic-${label}`,
        "data-note-index": String(i),
        "data-dynamic": label,
      });
      t.textContent = glyph;
      dG.appendChild(t);
    }
    root.appendChild(dG);
  }

  // Where this staff's lines end, for a system that draws its extent bar and
  // box around several staves (createNotationSystem).
  root._staffEnd = lineWidth;
  return root;
}
